
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, ThumbsUp, Shield, Calendar, TrendingUp, Award } from "lucide-react";
import { ProductReview } from "@/types/product";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { useReviewActions } from "@/hooks/useReviewActions";

interface ProductReviewsSectionProps {
  reviews: ProductReview[];
  averageRating: number;
  reviewCount: number;
  onWriteReview?: () => void;
}

const ProductReviewsSection = ({
  reviews,
  averageRating,
  reviewCount,
  onWriteReview
}: ProductReviewsSectionProps) => {
  const [visibleReviews, setVisibleReviews] = useState(5);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const { markHelpful } = useReviewActions();

  // Calculate rating distribution
  const ratingDistribution = Array.from({ length: 5 }, (_, i) => {
    const rating = 5 - i;
    const count = reviews.filter(r => r.rating === rating).length;
    const percentage = reviewCount > 0 ? (count / reviewCount) * 100 : 0;
    return { rating, count, percentage };
  });

  const filteredReviews = selectedRating 
    ? reviews.filter(r => r.rating === selectedRating)
    : reviews;

  const displayedReviews = filteredReviews.slice(0, visibleReviews);

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'sm') => {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6'
    };

    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            className={`${sizeClasses[size]} ${
              i < rating ? 'fill-luxury-gold text-luxury-gold' : 'text-muted-foreground/40'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Rating Summary */}
      <Card className="relative bg-gradient-luxury-subtle border-luxury-gold/10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-luxury-feature/20 pointer-events-none" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center justify-between text-2xl">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-modern bg-luxury-gold/10">
                <Award className="h-6 w-6 text-luxury-gold" />
              </div>
              <span>Customer Reviews</span>
            </div>
            {onWriteReview && (
              <Button 
                onClick={onWriteReview} 
                className="bg-luxury-gold hover:bg-luxury-gold-dark text-primary-foreground px-6"
              >
                Write a Review
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="relative space-y-8 p-8">
          {/* Overall Rating */}
          <motion.div 
            className="flex items-center gap-8 lg:gap-12"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center p-8 bg-luxury-gold/5 rounded-modern border border-luxury-gold/20">
              <motion.div 
                className="text-5xl font-bold text-luxury-gold mb-2"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3, type: "spring" }}
              >
                {averageRating.toFixed(1)}
              </motion.div>
              <motion.div 
                className="flex justify-center mb-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                {renderStars(Math.round(averageRating), 'lg')}
              </motion.div>
              <div className="text-muted-foreground font-medium">
                {reviewCount} reviews
              </div>
            </div>

            {/* Rating Distribution */}
            <div className="flex-1 space-y-4">
              {ratingDistribution.map(({ rating, count, percentage }, index) => (
                <motion.div 
                  key={rating} 
                  className="flex items-center gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedRating(selectedRating === rating ? null : rating)}
                    className={`h-auto p-3 rounded-modern transition-all duration-200 ${
                      selectedRating === rating 
                        ? 'bg-luxury-gold/10 border border-luxury-gold/30' 
                        : 'hover:bg-luxury-gold/5'
                    }`}
                  >
                    <span className="text-sm font-medium">{rating}</span>
                    <Star className="h-3 w-3 ml-2 fill-luxury-gold text-luxury-gold" />
                  </Button>
                  <div className="flex-1 bg-muted/50 rounded-full h-3 overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-luxury-gold to-luxury-gold-light"
                      style={{ width: `${percentage}%` }}
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 1, delay: 0.8 + index * 0.1, ease: "easeOut" }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground min-w-[3ch] font-medium">
                    {count}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Filter Info */}
          {selectedRating && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                Showing {selectedRating} star reviews ({filteredReviews.length})
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedRating(null)}
              >
                Show all reviews
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reviews List */}
      <div className="space-y-4">
        {displayedReviews.map((review) => (
          <Card key={review.id}>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Review Header */}
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      {renderStars(review.rating)}
                      {review.verified_purchase && (
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          <Shield className="h-3 w-3" />
                          Verified Purchase
                        </Badge>
                      )}
                      {review.is_featured && (
                        <Badge variant="default" className="text-xs">
                          Featured Review
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="font-medium">
                        {review.reviewer_name || 'Anonymous Customer'}
                      </span>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(review.created_at), 'MMM d, yyyy')}
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-1"
                    onClick={() => markHelpful.mutate(review.id)}
                    disabled={markHelpful.isPending}
                  >
                    <ThumbsUp className="h-3 w-3" />
                    <span className="text-xs">{review.helpful_count}</span>
                  </Button>
                </div>

                {/* Review Content */}
                <div className="space-y-2">
                  {review.title && (
                    <h4 className="font-semibold text-foreground">
                      {review.title}
                    </h4>
                  )}
                  {review.content && (
                    <p className="text-muted-foreground leading-relaxed">
                      {review.content}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Load More Button */}
      {filteredReviews.length > visibleReviews && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => setVisibleReviews(prev => prev + 5)}
          >
            Load More Reviews
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductReviewsSection;
